/**
 * Per-message debug log builder + downloader.
 *
 * Used by the gameplay message DEBUG menu (admins / world owners) to export the
 * complete data for a single message. For AI (narrator) messages the metadata
 * already carries the full AI-process trace — classification, director notes,
 * sub-agents, tools, token usage, state changes, etc. — so the exported log is
 * the whole message object plus a small export envelope.
 */

interface AnyMessage {
  id: string;
  adventure_id?: string;
  role?: string;
  content?: string;
  turn?: number;
  created_at?: string;
  metadata?: Record<string, unknown> | null;
  player_id?: string | null;
  [key: string]: unknown;
}

/** Build the structured debug-log payload for a single message. */
export function buildMessageDebugLog(msg: AnyMessage): Record<string, unknown> {
  const metadata = (msg.metadata ?? {}) as Record<string, unknown>;
  const isAi = msg.role === "narrator" || msg.role === "assistant";

  return {
    _export: {
      kind: "adventure-message-debug",
      version: 1,
      exported_at: new Date().toISOString(),
      is_ai_message: isAi,
    },
    message: {
      id: msg.id,
      adventure_id: msg.adventure_id ?? null,
      role: msg.role ?? null,
      turn: msg.turn ?? null,
      created_at: msg.created_at ?? null,
      player_id: msg.player_id ?? null,
      content: msg.content ?? "",
    },
    // Full metadata — for AI messages this contains the complete process trace
    // (intent classification, director notes, sub-agents, tools, tokens, state
    // changes, mechanic results, validity checks, dice rolls, timings, …).
    metadata,
  };
}

function sanitizeFilename(name: string): string {
  return name.replace(/[^a-zA-Z0-9_\- ]/g, "").replace(/\s+/g, "_").substring(0, 60);
}

/** Build the log and trigger a JSON file download in the browser. */
export function downloadMessageDebugLog(msg: AnyMessage): void {
  const payload = buildMessageDebugLog(msg);
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  const shortId = String(msg.id || "message").slice(0, 8);
  const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
  a.href = url;
  a.download = `message-debug-${sanitizeFilename(shortId)}-${stamp}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
